import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import api from './api';
import { Icons } from './Icons';

const ROLE_LABELS = { admin: 'Admin', helper: 'Helfer' };

function SortHeader({ label, field, currentSort, currentOrder, onSort, style }) {
  const isActive = currentSort === field;
  return (
    <th className={`th-sortable ${isActive ? 'th-sort-active' : ''}`} style={style} onClick={() => onSort(field)}>
      <span className="th-sort-inner">
        {label}
        <span className="sort-icon">{isActive ? (currentOrder === 'asc' ? '▲' : '▼') : '⇅'}</span>
      </span>
    </th>
  );
}

// === Box Form (Create / Edit) ===
export function BoxFormPage({ toast: addToast }) {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEdit = !!id;
  const [rooms, setRooms] = useState([]);
  const [form, setForm] = useState({
    number: '', label: '', room_id: '', contents: '', note: ''
  });
  const [photo, setPhoto] = useState(null);
  const [photoPreview, setPhotoPreview] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    api.get('/rooms').then(r => setRooms(r.data));
    if (isEdit) {
      api.get(`/boxes/${id}`).then(r => {
        const d = r.data;
        setForm({
          number: d.number, label: d.label || '',
          room_id: d.room_id || '', contents: d.contents || '', note: d.note || ''
        });
        if (d.photo) setPhotoPreview(`/api/uploads/${d.photo}`);
      });
    } else {
      // Fetch next box number suggestion
      api.get('/boxes/next-number').then(r => {
        setForm(f => ({ ...f, number: r.data.next }));
      });
    }
  }, [id, isEdit]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm(f => ({ ...f, [name]: value }));
  };

  const handlePhoto = (e) => {
    const file = e.target.files[0];
    if (file) { setPhoto(file); setPhotoPreview(URL.createObjectURL(file)); }
  };

  const handleSubmit = async (e) => {
    e.preventDefault(); setError(''); setSaving(true);
    try {
      const fd = new FormData();
      fd.append('number', form.number);
      if (form.label) fd.append('label', form.label);
      if (form.room_id) fd.append('room_id', form.room_id);
      fd.append('contents', form.contents);
      if (form.note) fd.append('note', form.note);
      if (photo) fd.append('photo', photo);

      if (isEdit) {
        await api.put(`/boxes/${id}`, fd);
        addToast('Kiste aktualisiert');
        navigate(`/boxes/${id}`);
      } else {
        const res = await api.post('/boxes', fd);
        addToast('Kiste erstellt');
        navigate(`/boxes/${res.data.id}`);
      }
    } catch (err) { setError(err.response?.data?.error || 'Fehler beim Speichern'); }
    finally { setSaving(false); }
  };

  return (
    <div className="page-container">
      <div className="page-header">
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <button className="btn btn-ghost btn-sm" onClick={() => navigate(isEdit ? `/boxes/${id}` : '/boxes')}><Icons.ChevronLeft /> Zurück</button>
          <h1 className="page-title">{isEdit ? 'Kiste bearbeiten' : 'Neue Kiste'}</h1>
        </div>
      </div>

      <div className="card"><div className="card-body">
        {error && <div className="alert alert-error">{error}</div>}
        <form onSubmit={handleSubmit}>
          <div className="form-row">
            <div className="form-group" style={{ maxWidth: 120 }}>
              <label className="form-label">Kistennummer *</label>
              <input className="form-input" name="number" value={form.number} onChange={handleChange} required style={{ fontSize: '1.2rem', fontWeight: 600, textAlign: 'center' }} />
            </div>
            <div className="form-group" style={{ flex: 1 }}>
              <label className="form-label">Bezeichnung (optional)</label>
              <input className="form-input" name="label" value={form.label} onChange={handleChange} placeholder="z.B. Küchenkram, Bücher Regal oben…" />
            </div>
            <div className="form-group">
              <label className="form-label">Zielraum</label>
              <select className="form-select" name="room_id" value={form.room_id} onChange={handleChange}>
                <option value="">– Raum wählen –</option>
                {rooms.map(r => <option key={r.id} value={r.id}>{r.name}</option>)}
              </select>
            </div>
          </div>

          <div className="form-group">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
              <label className="form-label" style={{ margin: 0 }}>Inhalt</label>
              {form.contents && (
                <button type="button" className="btn btn-ghost btn-sm" onClick={() => setForm(f => ({ ...f, contents: '' }))}>
                  <Icons.Trash /> Leeren
                </button>
              )}
            </div>
            <textarea className="form-input" name="contents" value={form.contents} onChange={handleChange}
              rows={8} placeholder="Inhalt der Kiste (per Sprache oder manuell eingeben)…"
              style={{ fontFamily: 'inherit', lineHeight: 1.6 }} />
          </div>

          <div className="form-group">
            <label className="form-label">Notiz (optional)</label>
            <input className="form-input" name="note" value={form.note} onChange={handleChange} placeholder="z.B. Vorsicht zerbrechlich…" />
          </div>

          <div className="form-group">
            <label className="form-label">Foto</label>
            <input type="file" accept="image/*" onChange={handlePhoto} className="form-input" />
            {photoPreview && <img src={photoPreview} alt="Vorschau" style={{ maxHeight: 200, borderRadius: 'var(--radius)', marginTop: 8, objectFit: 'contain' }} />}
          </div>

          <div className="btn-group" style={{ marginTop: 20 }}>
            <button className="btn btn-primary btn-lg" type="submit" disabled={saving}>
              {saving ? 'Wird gespeichert…' : isEdit ? 'Speichern' : 'Kiste erstellen'}
            </button>
            <button className="btn btn-secondary" type="button" onClick={() => navigate(isEdit ? `/boxes/${id}` : '/boxes')}>Abbrechen</button>
          </div>
        </form>
      </div></div>
    </div>
  );
}

// === Users Page (Admin) ===
export function UsersPage({ toast }) {
  const [users, setUsers] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState({ email: '', password: '', name: '', role: 'helper' });

  const fetchUsers = () => api.get('/auth/users').then(r => setUsers(r.data));
  useEffect(() => { fetchUsers(); }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editId) {
        const data = { name: form.name, role: form.role };
        if (form.password) data.password = form.password;
        await api.put(`/auth/users/${editId}`, data);
        toast('Benutzer aktualisiert');
      } else {
        await api.post('/auth/users', form);
        toast('Benutzer erstellt');
      }
      setShowForm(false); setEditId(null);
      setForm({ email: '', password: '', name: '', role: 'helper' });
      fetchUsers();
    } catch (err) { toast(err.response?.data?.error || 'Fehler', 'error'); }
  };

  const handleEdit = (u) => {
    setEditId(u.id);
    setForm({ email: u.email, password: '', name: u.name, role: u.role });
    setShowForm(true);
  };

  const handleDelete = async (u) => {
    if (!window.confirm(`Benutzer "${u.name}" löschen?`)) return;
    try { await api.delete(`/auth/users/${u.id}`); toast('Benutzer gelöscht'); fetchUsers(); }
    catch (err) { toast(err.response?.data?.error || 'Fehler', 'error'); }
  };

  const handleToggle = async (u) => {
    try { await api.put(`/auth/users/${u.id}`, { active: !u.active }); fetchUsers(); }
    catch (err) { toast(err.response?.data?.error || 'Fehler', 'error'); }
  };

  return (
    <div className="page-container">
      <div className="page-header">
        <h1 className="page-title">Benutzer</h1>
        <button className="btn btn-primary" onClick={() => { setShowForm(!showForm); setEditId(null); setForm({ email: '', password: '', name: '', role: 'helper' }); }}>
          <Icons.Plus /> Neuer Benutzer
        </button>
      </div>

      {showForm && (
        <div className="card" style={{ marginBottom: 20 }}><div className="card-body">
          <form onSubmit={handleSubmit}>
            <div className="form-row">
              <div className="form-group"><label className="form-label">Name</label><input className="form-input" value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} required /></div>
              <div className="form-group"><label className="form-label">E-Mail</label><input className="form-input" type="email" value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} required disabled={!!editId} /></div>
            </div>
            <div className="form-row">
              <div className="form-group"><label className="form-label">{editId ? 'Neues Passwort (leer = unverändert)' : 'Passwort'}</label><input className="form-input" type="password" value={form.password} onChange={e => setForm(f => ({ ...f, password: e.target.value }))} required={!editId} minLength={8} /></div>
              <div className="form-group"><label className="form-label">Rolle</label>
                <select className="form-select" value={form.role} onChange={e => setForm(f => ({ ...f, role: e.target.value }))}>
                  <option value="admin">Admin</option><option value="helper">Helfer</option>
                </select>
              </div>
            </div>
            <div className="btn-group"><button className="btn btn-primary" type="submit">{editId ? 'Speichern' : 'Erstellen'}</button>
              <button className="btn btn-secondary" type="button" onClick={() => setShowForm(false)}>Abbrechen</button></div>
          </form>
        </div></div>
      )}

      <div className="table-container"><table className="table"><thead><tr>
        <th>Name</th><th>E-Mail</th><th>Rolle</th><th>Status</th><th>Aktionen</th>
      </tr></thead><tbody>
        {users.map(u => (
          <tr key={u.id}>
            <td style={{ fontWeight: 500 }}>{u.name}</td>
            <td>{u.email}</td>
            <td><span className="badge">{ROLE_LABELS[u.role]}</span></td>
            <td><span className={`badge ${u.active ? 'badge-packed' : 'badge-open'}`}>{u.active ? 'Aktiv' : 'Deaktiviert'}</span></td>
            <td><div className="btn-group">
              <button className="btn btn-ghost btn-sm" onClick={() => handleEdit(u)}><Icons.Edit /></button>
              <button className="btn btn-ghost btn-sm" onClick={() => handleToggle(u)}>{u.active ? 'Deaktivieren' : 'Aktivieren'}</button>
              <button className="btn btn-ghost btn-sm" style={{ color: 'var(--danger)' }} onClick={() => handleDelete(u)}><Icons.Trash /></button>
            </div></td>
          </tr>
        ))}
      </tbody></table></div>
    </div>
  );
}

// === Rooms Management ===
export function RoomsPage({ toast }) {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [newName, setNewName] = useState('');
  const [editId, setEditId] = useState(null);
  const [editName, setEditName] = useState('');
  const [sortField, setSortField] = useState('name');
  const [sortOrder, setSortOrder] = useState('asc');

  const fetch = () => api.get('/rooms').then(r => setItems(r.data));
  useEffect(() => { fetch(); }, []);

  const handleSort = (field) => {
    if (sortField === field) setSortOrder(o => o === 'asc' ? 'desc' : 'asc');
    else { setSortField(field); setSortOrder('asc'); }
  };

  const sortedItems = [...items].sort((a, b) => {
    let cmp;
    if (sortField === 'name') cmp = a.name.localeCompare(b.name, 'de');
    else cmp = (a[sortField] || 0) - (b[sortField] || 0);
    return sortOrder === 'asc' ? cmp : -cmp;
  });

  const handleAdd = async (e) => {
    e.preventDefault();
    if (!newName.trim()) return;
    try { await api.post('/rooms', { name: newName.trim() }); setNewName(''); toast('Raum erstellt'); fetch(); }
    catch (err) { toast(err.response?.data?.error || 'Fehler', 'error'); }
  };

  const handleUpdate = async (id) => {
    if (!editName.trim()) return;
    try { await api.put(`/rooms/${id}`, { name: editName.trim() }); setEditId(null); toast('Aktualisiert'); fetch(); }
    catch (err) { toast(err.response?.data?.error || 'Fehler', 'error'); }
  };

  const handleDelete = async (id) => {
    try { await api.delete(`/rooms/${id}`); toast('Gelöscht'); fetch(); }
    catch (err) { toast(err.response?.data?.error || 'Fehler', 'error'); }
  };

  return (
    <div className="page-container">
      <div className="page-header"><h1 className="page-title">Räume</h1></div>
      <div className="card" style={{ marginBottom: 20 }}><div className="card-body">
        <form onSubmit={handleAdd} style={{ display: 'flex', gap: 12 }}>
          <input className="form-input" value={newName} onChange={e => setNewName(e.target.value)}
            placeholder="Neuer Raum…" style={{ flex: 1 }} />
          <button className="btn btn-primary" type="submit"><Icons.Plus /> Hinzufügen</button>
        </form>
      </div></div>

      <div className="table-container desktop-only"><table className="table"><thead><tr>
        <SortHeader label="Name" field="name" currentSort={sortField} currentOrder={sortOrder} onSort={handleSort} />
        <SortHeader label="Kisten" field="box_count" currentSort={sortField} currentOrder={sortOrder} onSort={handleSort} style={{ width: 140 }} />
        <SortHeader label="Einzelstücke" field="item_count" currentSort={sortField} currentOrder={sortOrder} onSort={handleSort} style={{ width: 140 }} />
        <th style={{ width: 150 }}>Aktionen</th>
      </tr></thead><tbody>
        {sortedItems.map(item => (
          <tr key={item.id}>
            <td>{editId === item.id ? (
              <input className="form-input" value={editName} onChange={e => setEditName(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && handleUpdate(item.id)} autoFocus />
            ) : <span style={{ fontWeight: 500 }}>{item.name}</span>}</td>
            <td><span className="room-link" onClick={() => navigate(`/boxes?room=${item.id}`)} title={`Kisten für ${item.name} anzeigen`}>{item.box_count} Kisten</span></td>
            <td><span className="room-link" onClick={() => navigate(`/items?room=${item.id}`)} title={`Einzelstücke für ${item.name} anzeigen`}>{item.item_count} Einzelstücke</span></td>
            <td><div className="btn-group">
              {editId === item.id ? (
                <><button className="btn btn-primary btn-sm" onClick={() => handleUpdate(item.id)}>Speichern</button>
                  <button className="btn btn-ghost btn-sm" onClick={() => setEditId(null)}>Abbrechen</button></>
              ) : (
                <><button className="btn btn-ghost btn-sm" onClick={() => { setEditId(item.id); setEditName(item.name); }}><Icons.Edit /></button>
                  <button className="btn btn-ghost btn-sm" style={{ color: 'var(--danger)' }} onClick={() => handleDelete(item.id)}><Icons.Trash /></button></>
              )}
            </div></td>
          </tr>
        ))}
      </tbody></table></div>

      <div className="mobile-only room-card-list">
        {sortedItems.map(item => (
          <div key={item.id} className="room-card">
            {editId === item.id ? (
              <div className="room-card-edit">
                <input className="form-input" value={editName} onChange={e => setEditName(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && handleUpdate(item.id)} autoFocus />
                <div className="room-card-actions">
                  <button className="btn btn-primary btn-sm" onClick={() => handleUpdate(item.id)}>Speichern</button>
                  <button className="btn btn-ghost btn-sm" onClick={() => setEditId(null)}>Abbrechen</button>
                </div>
              </div>
            ) : (
              <>
                <div className="room-card-main">
                  <div className="room-card-name">{item.name}</div>
                  <div className="room-card-counts">
                    <span className="room-link" onClick={() => navigate(`/boxes?room=${item.id}`)}>{item.box_count} Kisten</span>
                    <span className="room-link" onClick={() => navigate(`/items?room=${item.id}`)}>{item.item_count} Einzelstücke</span>
                  </div>
                </div>
                <div className="room-card-actions">
                  <button className="btn btn-ghost btn-sm" onClick={() => { setEditId(item.id); setEditName(item.name); }} aria-label="Bearbeiten"><Icons.Edit /></button>
                  <button className="btn btn-ghost btn-sm" style={{ color: 'var(--danger)' }} onClick={() => handleDelete(item.id)} aria-label="Löschen"><Icons.Trash /></button>
                </div>
              </>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

// === Settings (Change Password) ===
export function SettingsPage({ toast }) {
  const [form, setForm] = useState({ currentPassword: '', newPassword: '', confirmPassword: '' });
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault(); setError('');
    if (form.newPassword !== form.confirmPassword) { setError('Passwörter stimmen nicht überein'); return; }
    try {
      await api.put('/auth/change-password', { currentPassword: form.currentPassword, newPassword: form.newPassword });
      toast('Passwort geändert');
      setForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
    } catch (err) { setError(err.response?.data?.error || 'Fehler'); }
  };

  return (
    <div className="page-container">
      <div className="page-header"><h1 className="page-title">Passwort ändern</h1></div>
      <div className="card"><div className="card-body" style={{ maxWidth: 400 }}>
        {error && <div className="alert alert-error">{error}</div>}
        <form onSubmit={handleSubmit}>
          <div className="form-group"><label className="form-label">Aktuelles Passwort</label><input className="form-input" type="password" value={form.currentPassword} onChange={e => setForm(f => ({ ...f, currentPassword: e.target.value }))} required /></div>
          <div className="form-group"><label className="form-label">Neues Passwort</label><input className="form-input" type="password" value={form.newPassword} onChange={e => setForm(f => ({ ...f, newPassword: e.target.value }))} required minLength={8} /></div>
          <div className="form-group"><label className="form-label">Passwort bestätigen</label><input className="form-input" type="password" value={form.confirmPassword} onChange={e => setForm(f => ({ ...f, confirmPassword: e.target.value }))} required /></div>
          <button className="btn btn-primary" type="submit">Passwort ändern</button>
        </form>
      </div></div>
    </div>
  );
}

// === Item Form (Create / Edit) – Einzelstücke ===
export function ItemFormPage({ toast: addToast }) {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEdit = !!id;
  const [form, setForm] = useState({ name: '', room_id: '', location: 'wuppertal', note: '' });
  const [rooms, setRooms] = useState([]);
  const [photo, setPhoto] = useState(null);
  const [existingPhoto, setExistingPhoto] = useState(null);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get('/rooms').then(r => setRooms(r.data));
    if (isEdit) {
      api.get(`/items/${id}`).then(r => {
        const { name, room_id, location, note, photo } = r.data;
        setForm({ name: name || '', room_id: room_id ? String(room_id) : '', location: location || 'wuppertal', note: note || '' });
        if (photo) setExistingPhoto(photo);
      }).catch(() => { addToast('Einzelstück nicht gefunden', 'error'); navigate('/items'); });
    }
  }, [id, isEdit, navigate, addToast]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm(f => ({ ...f, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault(); setError(''); setSaving(true);
    try {
      const formData = new FormData();
      formData.append('name', form.name);
      if (form.room_id) formData.append('room_id', form.room_id);
      if (form.location) formData.append('location', form.location);
      if (form.note) formData.append('note', form.note);
      if (photo) formData.append('photo', photo);

      if (isEdit) {
        await api.put(`/items/${id}`, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
        addToast('Einzelstück aktualisiert');
      } else {
        await api.post('/items', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
        addToast('Einzelstück erstellt');
      }
      navigate('/items');
    } catch (err) {
      setError(err.response?.data?.error || 'Fehler beim Speichern');
    }
    setSaving(false);
  };

  return (
    <div className="page-container">
      <div className="page-header">
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <button className="btn btn-ghost" onClick={() => navigate('/items')}><Icons.ChevronLeft /> Zurück</button>
          <h1 className="page-title">{isEdit ? 'Einzelstück bearbeiten' : 'Neues Einzelstück'}</h1>
        </div>
      </div>

      {error && <div className="alert alert-error">{error}</div>}

      <div className="card"><div className="card-body">
        <form onSubmit={handleSubmit}>
          <div className="form-row">
            <div className="form-group" style={{ flex: 2 }}>
              <label className="form-label required">Name</label>
              <input className="form-input" name="name" value={form.name} onChange={handleChange}
                placeholder="z.B. Couch, Stehlampe, großes Bild…" required />
            </div>
            <div className="form-group">
              <label className="form-label">Zielraum</label>
              <select className="form-select" name="room_id" value={form.room_id} onChange={handleChange}>
                <option value="">– Raum wählen –</option>
                {rooms.map(r => <option key={r.id} value={r.id}>{r.name}</option>)}
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">Standort</label>
              <select className="form-select" name="location" value={form.location} onChange={handleChange}>
                <option value="wuppertal">Wuppertal</option>
                <option value="friedemannweg">Friedemannweg</option>
                <option value="waldemarstrasse">Waldemarstraße</option>
              </select>
            </div>
          </div>

          <div className="form-group">
            <label className="form-label">Notiz (optional)</label>
            <textarea className="form-input" name="note" value={form.note} onChange={handleChange}
              placeholder="z.B. Braucht 2 Träger, zerbrechlich…" rows={3} />
          </div>

          <div className="form-group">
            <label className="form-label">Foto (optional)</label>
            {existingPhoto && !photo && <img src={`/api/uploads/${existingPhoto}`} alt="" style={{ maxWidth: 200, maxHeight: 200, borderRadius: 'var(--radius)', marginBottom: 10, display: 'block' }} />}
            <input type="file" accept="image/*" onChange={e => setPhoto(e.target.files[0])} />
          </div>

          <div className="btn-group" style={{ marginTop: 20 }}>
            <button className="btn btn-primary btn-lg" type="submit" disabled={saving}>{saving ? 'Wird gespeichert…' : isEdit ? 'Speichern' : 'Einzelstück erstellen'}</button>
            <button className="btn btn-secondary" type="button" onClick={() => navigate('/items')}>Abbrechen</button>
          </div>
        </form>
      </div></div>
    </div>
  );
}
